import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  Account,
  AccountsRequestContext,
  AccountsSetValues,
  isEmptyAccountTypeScope,
  scopeAccountTypeFilter,
} from '@helios/generic-components'
import { Filter, HeliosServerSideRequest } from '@helios/server-side-util'
import { Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'

/** @internal */
export const ACCOUNT_LIST_URL = `helios-accounts`

/** @internal */
export const ACCOUNTS_SET_VALUES_URL = `${ACCOUNT_LIST_URL}/set-values`

/** @internal */
export const ACCOUNTS_AGGREGATES = `${ACCOUNT_LIST_URL}`

/** @internal */
export interface GetAccountsResponse {
  data: Account[]
}

@Injectable({
  providedIn: 'root',
})
export class HttpAccountListService {
  constructor(private readonly http: HttpClient) {}

  private getAccounts(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): Observable<Account[]> {
    return this.http.post<{ data: Account[] }>(ACCOUNT_LIST_URL, request).pipe(
      map(response => {
        return response.data ?? []
      }),
    )
  }

  /**
   * Fetches accounts with the user's accountType column filter narrowed to the
   * types this panel shows. @see scopeAccountTypeFilter
   */
  private getScopedAccounts(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
    allowedValues: Account['accountType'][],
  ): Observable<Account[]> {
    const accountType = scopeAccountTypeFilter(
      request.filter?.accountType,
      allowedValues,
    )

    if (isEmptyAccountTypeScope(accountType)) {
      return of([])
    }

    return this.getAccounts({
      ...request,
      filter: {
        ...request.filter,
        accountType: accountType as Filter,
      },
    })
  }

  /**
   * Retrieves portfolio and simple accounts.
   *
   * @param request - Request for fetching accounts.
   * @returns Observable containing an array of portfolio and simple accounts.
   */
  getPortfolioAndSimpleAccounts(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): Observable<Account[]> {
    return this.getScopedAccounts(request, [
      'simple',
      'sleeveAggregate',
      'sleeveSimple',
    ])
  }

  /**
   * Retrieves aggregate accounts.
   *
   * @param request - Request for fetching accounts.
   * @returns Observable containing an array of aggregate accounts.
   */
  getAggregateAccounts(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): Observable<Account[]> {
    return this.getScopedAccounts(request, ['aggregate'])
  }

  /**
   * Fetches ag-grid filter set values for the account list.
   *
   * @returns ag-grid filter set values. For example the backend might tell us that for accountType we can only filter
   * these options: 'simple' | 'aggregate'.
   */
  getAccountsSetValues(): Observable<AccountsSetValues> {
    return this.http.get<AccountsSetValues>(ACCOUNTS_SET_VALUES_URL)
  }

  getAccountListCountForFilter(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): Observable<number> {
    // Mirrors getScopedAccounts so the status bar total matches the rows shown.
    if (isEmptyAccountTypeScope(request.filter?.accountType)) {
      return of(0)
    }

    return this.http
      .post<{ rowCount: number }>(`${ACCOUNT_LIST_URL}/summaries`, request)
      .pipe(map(response => response.rowCount))
  }
}
